import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, TrendingUp, Handshake, Building2, Globe2, Target, Users, ArrowRight, Quote } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline, SectionHeading, GoldRule } from "@/components/Bits";

const PILLARS = [
  {
    icon: Building2,
    title: "Enterprise Development",
    description: "Building profitable, Kingdom-aligned businesses across ministry, media, education and technology in Africa and beyond.",
  },
  {
    icon: TrendingUp,
    title: "Marketplace Leadership",
    description: "Equipping believers to lead in business the way David led Israel — with excellence, integrity, and Kingdom purpose.",
  },
  {
    icon: Globe2,
    title: "Cross-Border Ventures",
    description: "Structuring partnerships that connect Africa with Europe, the Americas and Asia — building bridges of impact.",
  },
  {
    icon: Users,
    title: "Executive Mentorship",
    description: "Coaching founders and executives through the seasons of scaling, succession, and stewardship in the Kingdom economy.",
  },
];

const VENTURES = [
  {
    name: "House of Blessing Ministries",
    role: "Founder & Apostle",
    scope: "Ministry · Publishing · Media",
    summary: "The flagship ministry — anchoring apostolic teaching, publishing, worship, and leadership development across nations.",
  },
  {
    name: "AI Academy",
    role: "Founder & Lead Instructor",
    scope: "EdTech · AI & Innovation",
    summary: "Training pastors, marketplace leaders, and next-gen entrepreneurs to master AI and lead the transformation of industries.",
  },
  {
    name: "Blaise Judah Consulting",
    role: "Principal Consultant",
    scope: "Advisory · Leadership · Strategy",
    summary: "Strategic advisory for churches, businesses, and public institutions on leadership, transformation, and Kingdom impact.",
  },
  {
    name: "Judah Publishing",
    role: "Author & Publisher",
    scope: "Books · Digital Products",
    summary: "Books and resources that renew minds and equip leaders — distributed globally through the Store and partner channels.",
  },
];

const PHILOSOPHY = [
  { n: "01", text: "Business is worship. Excellence, integrity, and stewardship are non-negotiable." },
  { n: "02", text: "Profitability without purpose is poverty. Every venture must advance the Kingdom." },
  { n: "03", text: "The renewed mind builds renewed markets. Romans 12:1–2 is our operating system." },
  { n: "04", text: "Africa is not a charity case. Africa is a treasury God is unlocking for the nations." },
];

export default function Business() {
  return (
    <div data-testid="business-page">
      <PageHero
        overline="Business & Enterprise"
        title={<>Kingdom vision. <span className="font-accent text-gold-gradient italic">Marketplace power.</span></>}
        subtitle="Apostle Blaise Judah is not only a preacher — he is a founder, strategist, and marketplace leader. His companies, ventures, and mentorship equip Kingdom leaders to build with excellence in ministry, business, technology, and beyond."
      />

      {/* Opening quote */}
      <section className="mx-auto max-w-4xl px-6 md:px-10 py-20 md:py-28 text-center" data-testid="business-quote">
        <Reveal>
          <Quote size={26} className="text-gold-500/60 mx-auto mb-6" strokeWidth={1.4} />
          <p className="font-accent italic text-2xl md:text-3xl lg:text-4xl text-white/90 leading-snug">
            "The believer was never called to survive the marketplace. He was called to shape it — as a son of the Kingdom, not as a slave of the system."
          </p>
          <p className="mt-6 text-sm tracking-[0.2em] text-gold-400 uppercase">— Apostle Blaise Judah</p>
        </Reveal>
      </section>

      {/* Pillars */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24" data-testid="business-pillars">
        <Reveal className="mb-14">
          <Overline>The Four Pillars</Overline>
          <SectionHeading className="mt-4">Where the Kingdom meets the marketplace.</SectionHeading>
          <GoldRule className="mt-6" />
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={(i % 4) * 0.05}>
              <div className="h-full border border-gold-500/20 bg-ink-paper/40 p-7 transition-colors duration-300 hover:border-gold-500/50" data-testid={`pillar-${i}`}>
                <p.icon size={22} className="text-gold-500 mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-white leading-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Ventures */}
      <section className="border-t border-gold-500/20 py-24 md:py-32" data-testid="business-ventures">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Overline>The Portfolio</Overline>
              <SectionHeading className="mt-4">Companies, ventures, and platforms.</SectionHeading>
            </div>
            <p className="max-w-sm text-sm text-white/55">
              A growing ecosystem of Kingdom enterprises — each anchored in vision, run with excellence, and reinvested for global impact.
            </p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2">
            {VENTURES.map((v, i) => (
              <Reveal key={v.name} delay={(i % 2) * 0.05}>
                <motion.article
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.4 }}
                  className="h-full border border-gold-500/20 bg-ink-paper/40 p-8"
                  data-testid={`venture-${i}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <Briefcase size={20} className="text-gold-500 shrink-0 mt-1" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30 px-2.5 py-1">{v.scope}</span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-white leading-tight">{v.name}</h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gold-400">{v.role}</p>
                  <p className="mt-4 text-sm text-white/70 leading-relaxed">{v.summary}</p>
                </motion.article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="border-t border-gold-500/20 py-24 md:py-32" data-testid="business-philosophy">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <Reveal className="mb-14">
            <Overline>Kingdom Business Philosophy</Overline>
            <SectionHeading className="mt-4">Four convictions guiding every deal.</SectionHeading>
            <GoldRule className="mt-6" />
          </Reveal>
          <div className="space-y-8">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.n} delay={(i % 4) * 0.05}>
                <div className="flex items-baseline gap-8 border-b border-gold-500/15 pb-6" data-testid={`philosophy-${i}`}>
                  <span className="font-serif text-5xl text-gold-500/70 shrink-0 w-16">{p.n}</span>
                  <p className="text-lg md:text-xl text-white/85 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Engagements CTA */}
      <section className="border-t border-gold-500/20 py-24 md:py-32" data-testid="business-cta">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-6 md:grid-cols-3">
          {[
            { icon: Handshake, title: "Partnership", body: "Long-term collaboration with churches, foundations, and Kingdom-aligned businesses.", link: "/contact", cta: "Explore partnership" },
            { icon: Target, title: "Consulting", body: "Advisory engagements for leaders, executives, and boards navigating transformation.", link: "/consulting", cta: "See consulting" },
            { icon: Users, title: "Speaking", body: "Keynotes, board addresses, conferences, and closed-door executive sessions.", link: "/contact", cta: "Invite Apostle" },
          ].map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.05}>
              <Link
                to={c.link}
                data-testid={`cta-${c.title.toLowerCase()}`}
                className="group flex h-full flex-col border border-gold-500/20 bg-ink-paper/40 p-8 transition-colors duration-300 hover:border-gold-500/60"
              >
                <c.icon size={24} className="text-gold-500 mb-5" strokeWidth={1.4} />
                <h3 className="font-serif text-2xl text-white leading-tight">{c.title}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed flex-1">{c.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold-300 group-hover:text-gold-200">
                  {c.cta} <ArrowRight size={12} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
